import Axios, { AxiosError, AxiosResponse } from "axios";
import { ActionCreator, Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";
import { notification } from "antd";

import { UserProfileAxios } from "../Contracts/Axios/UserProfileAxios";
import { IUser, IUserProfile, IServiceResult } from "../Models/Models";
import * as types from "./Types";
import { IOktaMFAFactor } from "../Models/OktaModels/IOktaMFAFactor";
import { IOktaMFAFactorPushVerifyResult } from "../Models/OktaModels/IOktaMFAFactorPushVerifyResult";
import { IUserShareSettings } from "../Models/IUserShareSettings";

// UPDATE
export const updateUserProfile: ActionCreator<ThunkAction<any, any, any, any>>
  = (
      profileUpdateObj: IUserProfile,
      shareSettingsUpdateObj: IUserShareSettings
  ) => {
      return (dispatch: Dispatch): void => {
          const userProfileAjax = new UserProfileAxios();
          const promises = [];
          promises.push(userProfileAjax.UpdateUserProfile(profileUpdateObj));
          promises.push(
              userProfileAjax.UpdateShareSettings(shareSettingsUpdateObj)
          );

          dispatch(updateSaveProfileLoading(true));

          Axios.all(promises)
              .then(
                  Axios.spread(
                      (...responses: IServiceResult<IUser | IUserShareSettings>[]) => {
                          const profileResponse = responses[0] as IServiceResult<IUser>;
                          const shareSettingsResponse
                = responses[1] as IServiceResult<IUserShareSettings>;

                          if (profileResponse.result
                            && shareSettingsResponse.result) {

                              if (profileUpdateObj.mfaEnrollUnenroll !== null) {
                                  const modalText = profileUpdateObj.mfaEnrollUnenroll
                                      ? `User profile updated successfully, and multi-factor authentication has been enabled on your account!
								You will be prompted to set up your factors (e.g. text message, "Okta Verify" app) on your next log in.
								If you would like to do this now please log out of SCOUT and then log back in.`
                                      : `User profile updated successfully, and multi-factor authentication has been disabled on your account.`;

                                  dispatch(setMfaEnrollmentModalText(modalText));
                                  dispatch(setMFAResult(""));
                                  dispatch(setShowMfaEnrollmentModal(true));
                              } else {
                                  if (!profileResponse.result){
                                      notification.open({
                                          message: "Not Updated",
                                          description: profileResponse.messages[0] || "Invalid",
                                      });
                                  } else {
                                      notification.open({
                                          message: "Success!",
                                          description: "Your user profile has been updated",
                                      });
                                  }
                                  dispatch(updateUserProfileSuccess(profileResponse));
                                  dispatch(
                                      updateUserShareSettingsSuccess(shareSettingsResponse)
                                  );
                              }
                          } else {
                              const modalText
                  = "There was an error updating your profile: "
                  + profileResponse.messages[0];

                              dispatch(setMfaEnrollmentModalText(modalText));
                              dispatch(setMFAResult("ERROR"));
                              dispatch(setShowMfaEnrollmentModal(true));
                          }
                          dispatch(updateSaveProfileLoading(false));
                      }
                  )
              )
              .catch((error: AxiosError) => {
                  dispatch(updateUserProfileFailure(error));
                  dispatch(updateSaveProfileLoading(false));
              });
      };
  };

export const updateUserAvatar: ActionCreator<ThunkAction<any, any, any, any>>
  = (avatarImg: File) => {
      return (dispatch: Dispatch): void => {
          const userProfileAjax = new UserProfileAxios();
          const promises = [];
          promises.push(userProfileAjax.UpdateUserAvatar(avatarImg));

          Axios.all(promises)
              .then(
                  Axios.spread((response: AxiosResponse) => {
                      notification.open({
                          message: "Success!",
                          description: "Your profile picture has been updated",
                      });
                      dispatch(updateUserAvatarSuccess(response));
                  })
              )
              .catch((error: AxiosError) => {
                  dispatch(updateUserAvatarFailure(error));
              });
      };
  };

export const updateShareImage: ActionCreator<ThunkAction<any, any, any, any>>
  = (shareImg: File) => {
      return (dispatch: Dispatch): void => {

          dispatch(updateSaveProfileLoading(true));

          const userProfileAjax = new UserProfileAxios();
          const promises = [];
          promises.push(userProfileAjax.UpdateShareImage(shareImg));

          Axios.all(promises)
              .then(
                  Axios.spread((response: AxiosResponse) => {
                      notification.open({
                          message: "Success!",
                          description: "Your Share Image has been updated.",
                      });
                      dispatch(updateShareImageSuccess(response));
                  })
              )
              .catch((error: AxiosError) => {
                  notification.open({
                      message: "Failure",
                      description: `Your Share Image couldn't be updated. ${error.message}`,
                  });
                  dispatch(updateShareImageFailure(error));
              })
              .finally(() => {
                  dispatch(updateSaveProfileLoading(false));
              });
      };
  };

export const resetPassword: ActionCreator<ThunkAction<any, any, any, any>>
  = () => {
      return (dispatch: Dispatch): void => {
          const userProfileAjax = new UserProfileAxios();
          const promises = [];
          promises.push(userProfileAjax.ResetPassword());

          Axios.all(promises)
              .then(
                  Axios.spread((response: {
                      data: boolean,
                      result: boolean,
                      messages: any[]
                  }) => {
                      if (response.data) {
                          notification.open({
                              message: "An email has been sent with steps to reset your password.",
                              description: "Follow the instructions in the email to complete your password reset process.",
                          });
                          dispatch(resetPasswordSuccess(response));
                      } else {
                          notification.open({
                              message: "Password was not reset",
                              description: response.messages[0],
                          });
                      }
                  })
              )
              .catch((error: AxiosError) => {
                  notification.open({
                      message: "Password was not reset",
                      description: error.message,
                  });
                  dispatch(resetPasswordFailure(error));
              });
      };
  };

// DELETE
export const deleteUserAvatar: ActionCreator<ThunkAction<any, any, any, any>>
  = (avatarStr: string) => {
      return (dispatch: Dispatch): void => {
          const userProfileAjax = new UserProfileAxios();
          const promises = [];
          promises.push(userProfileAjax.DeleteUserAvatar(avatarStr));

          Axios.all(promises)
              .then(
                  Axios.spread((response: AxiosResponse) => {
                      dispatch(deleteUserAvatarSuccess(response));
                  })
              )
              .catch((error: AxiosError) => {
                  dispatch(deleteUserAvatarFailure(error));
              });
      };
  };

export const deleteShareImage: ActionCreator<ThunkAction<any, any, any, any>>
  = (shareStr: string) => {
      return (dispatch: Dispatch): void => {

          dispatch(updateSaveProfileLoading(true));

          const userProfileAjax = new UserProfileAxios();
          const promises = [];
          promises.push(userProfileAjax.DeleteShareImage(shareStr));

          Axios.all(promises)
              .then(
                  Axios.spread((response: AxiosResponse) => {
                      notification.open({
                          message: "Success!",
                          description: "Share Image deleted.",
                      });
                      dispatch(deleteShareImageSuccess(response));
                  })
              )
              .catch((error: AxiosError) => {
                  notification.open({
                      message: "Failure",
                      description: `Share Image not deleted. ${error.message}`,
                  });
                  dispatch(deleteShareImageFailure(error));
              })
              .finally(() => {
                  dispatch(updateSaveProfileLoading(false));
              });
      };
  };

//MFA
export const GetMFAFactors: ActionCreator<ThunkAction<any, any, any, any>> = (
    oktaUserId: string
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setAllMFAFactors(null));
        dispatch(setMFAResult(""));

        Axios.get(`/Users/GetMFAFactors?oktaUserId=${oktaUserId}`)
            .then((response: AxiosResponse<IServiceResult<IOktaMFAFactor[]>>) => {
                const factors = response.data.data;
                dispatch(setAllMFAFactors(factors));
            })
            .catch((error) => {
                dispatch(setAllMFAFactors(null));
            });
    };
};

export const IssueMFAChallenge: ActionCreator<ThunkAction<any, any, any, any>>
  = (factor: IOktaMFAFactor) => {
      return (dispatch: Dispatch<any>) => {
          dispatch(setMFAResult(""));

          Axios.post("/Users/IssueMFAChallenge", factor).catch((error) => {
              console.error("Error issuing MFA challenge");
              console.error(error);
          });
      };
  };

export const IssuePushMFAChallenge: ActionCreator<
  ThunkAction<any, any, any, any>
> = (factor: IOktaMFAFactor) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setMFAResult("WAITING"));
        dispatch(setWaitingForMFAResponse(true));

        Axios.post("/Users/IssuePushMFAChallenge", factor)
            .then(
                (
                    response: AxiosResponse<
            IServiceResult<IOktaMFAFactorPushVerifyResult>
          >
                ) => {
                    const verifyResult = response.data.data;
                    dispatch(PollForPushResponse(verifyResult));
                }
            )
            .catch((error) => {
                console.error("Error issuing MFA challenge");
                console.error(error);
            });
    };
};

export const SubmitMFAChallengeResponse: ActionCreator<
  ThunkAction<any, any, any, any>
> = (factor: IOktaMFAFactor, challengeResponse: string) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setWaitingForMFAResponse(true));

        Axios.post("/Users/SubmitMFAChallengeResponse", {
            factor: factor,
            response: challengeResponse,
        })
            .then((response: AxiosResponse<IServiceResult<string>>) => {
                const message = response.data.data;
                dispatch(setMFAResult(message));
                dispatch(setWaitingForMFAResponse(false));
            })
            .catch((error) => {
                dispatch(
                    setMFAResult(
                        "Error communicating with authorization server. Please try again later."
                    )
                );
                dispatch(setWaitingForMFAResponse(false));
            });
    };
};

export const getUserShareSettings: ActionCreator<
  ThunkAction<any, any, any, any>
> = () => {
    return (dispatch: Dispatch<any>) => {
        const axios = new UserProfileAxios();
        const promise = axios.GetShareSettings();

        promise
            .then((response: IServiceResult<IUserShareSettings>) => {
                dispatch(getUserShareSettingsSuccess(response));
            })
            .catch((error: any) => {
                dispatch(getUserShareSettingsFailure(error));
            });
    };
};

export const PollForPushResponse: ActionCreator<
  ThunkAction<any, any, any, any>
> = (verifyResult: IOktaMFAFactorPushVerifyResult) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setWaitingForMFAResponse(true));

        Axios.post("/Users/PollForPushResponse", verifyResult)
            .then((response: AxiosResponse<IServiceResult<string>>) => {
                const message = response.data.data;
                dispatch(setMFAResult(message));
                dispatch(setWaitingForMFAResponse(false));
            })
            .catch((error) => {
                dispatch(
                    setMFAResult(
                        "Error communicating with authorization server. Please try again later."
                    )
                );
                dispatch(setWaitingForMFAResponse(false));
            });
    };
};
const updateSaveProfileLoading = (flag: boolean) => ({
    type: types.UPDATE_SAVE_PROFILE_LOADING,
    data: flag,
});
//------------------------------------------------ SUCCESS
const updateUserProfileSuccess = (data: IServiceResult<IUser>) => ({
    type: types.UPDATE_USER_PROFILE_SUCCESS,
    response: data,
});

const updateUserShareSettingsSuccess = (
    data: IServiceResult<IUserShareSettings>
) => ({
    type: types.UPDATE_USER_SHARE_SETTINGS_SUCCESS,
    data: data,
});

const getUserShareSettingsSuccess = (
    data: IServiceResult<IUserShareSettings>
) => ({
    type: types.GET_USER_SHARE_SETTINGS_SUCCESS,
    data: data,
});

const getUserShareSettingsFailure = (
    data: IServiceResult<IUserShareSettings>
) => ({
    type: types.GET_USER_SHARE_SETTINGS_FAILURE,
    data: data,
});

const resetPasswordSuccess = (data: IServiceResult<boolean>) => ({
    type: types.RESET_PASSWORD_SUCCESS,
    response: data,
});
const updateUserAvatarSuccess = (data: AxiosResponse) => ({
    type: types.UPDATE_USER_AVATAR_SUCCESS,
    response: data,
});
const deleteUserAvatarSuccess = (data: AxiosResponse) => ({
    type: types.DELETE_USER_AVATAR_SUCCESS,
    response: data,
});

const updateShareImageSuccess = (data: AxiosResponse) => ({
    type: types.UPDATE_SHARE_IMAGE_SUCCESS,
    response: data,
});
const deleteShareImageSuccess = (data: AxiosResponse) => ({
    type: types.DELETE_SHARE_IMAGE_SUCCESS,
    response: data,
});
//------------------------------------------------ FAILURE
const updateUserProfileFailure = (error: AxiosError) => ({
    type: types.UPDATE_USER_PROFILE_FAILURE,
    response: error,
});
const resetPasswordFailure = (error: AxiosError) => ({
    type: types.RESET_PASSWORD_FAILURE,
    response: error,
});
const updateUserAvatarFailure = (error: AxiosError) => ({
    type: types.UPDATE_USER_AVATAR_FAILURE,
    response: error,
});
const deleteUserAvatarFailure = (error: AxiosError) => ({
    type: types.DELETE_USER_AVATAR_FAILURE,
    response: error,
});
const updateShareImageFailure = (error: AxiosError) => ({
    type: types.UPDATE_SHARE_IMAGE_FAILURE,
    response: error,
});
const deleteShareImageFailure = (error: AxiosError) => ({
    type: types.DELETE_SHARE_IMAGE_FAILURE,
    response: error,
});
export const setAllMFAFactors = (factors: IOktaMFAFactor[]) => {
    return {
        type: types.USER_PROFILE_SET_MFA_FACTORS,
        data: factors,
    };
};
export const setSelectedFactorID = (id: string) => ({
    type: types.USER_PROFILE_SET_SELECTED_MFA_FACTOR_ID,
    data: id,
});
export const setWaitingForMFAResponse = (waiting: boolean) => ({
    type: types.USER_PROFILE_SET_WAITING_FOR_MFA_RESPONSE,
    data: waiting,
});
const setMFAResult = (result: string) => ({
    type: types.USER_PROFILE_SET_MFA_RESULT,
    data: result,
});
export const setShowMfaEnrollmentModal = (show: boolean) => ({
    type: types.USER_PROFILE_SET_SHOW_MFA_ENROLLMENT_MODAL,
    data: show,
});
const setMfaEnrollmentModalText = (text: string) => ({
    type: types.USER_PROFILE_SET_MFA_ENROLLMENT_MODAL_TEXT,
    data: text,
});
export const setMfaEnrollmentUpdatedThisSession = (updated: boolean) => ({
    type: types.USER_PROFILE_SET_MFA_ENROLLMENT_UPDATED_THIS_SESSION,
    data: updated,
});
